<template>
    <div class="card-creator">
        <div class="cr-title">
            <v-icon v-if="!stepperMode" size="20" color="accent">mdi-credit-card-outline</v-icon>
            {{ $t('payment.add_payment_method') }}
        </div>

        <div class="cr-fields">
            <kimsa-text-field
                :label="$t('payment.card_holder')"
                :placeholder="$t('payment.card_holder_placeholder')"
                :value="holderName"
                @change="holderName = $event"
                :rules="[v => !!v || $t('rules.required')]"
                clearable
            />

            <div class="cr-field">
                <span class="cr-label">{{ $t('payment.card_data') }}</span>
                <div id="card-element"></div>
            </div>
        </div>

        <v-slide-x-transition>
            <div v-show="!!error" class="cr-error">
                <v-icon size="18" color="error">mdi-alert-circle-outline</v-icon>
                <span>{{ error }}</span>
            </div>
        </v-slide-x-transition>

        <v-btn v-if="!noSaveButton" block depressed height="48" class="normal-btn cr-save black--text"
            :disabled="!complete || !holderName" @click="send" :loading="loading">
            <v-icon left size="20">mdi-credit-card-plus-outline</v-icon>
            {{ $t('common.save') }}
        </v-btn>
    </div>
</template>

<script>
import {mapState} from 'vuex'
import {loadStripe} from '@stripe/stripe-js';
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import {savePaymentMethod} from "@/utils/Plans";

export default {
    name: "CardCreator",
    props: {
        noSaveButton: {
            type: Boolean,
            default: false
        },
        defaultName: {
            type: String,
            default: ''
        },
        stepperMode: {
            type: Boolean,
            default: false
        }
    },
    components: {KimsaTextField},
    data(vm) {
        return {
            holderName: '',
            stripe: null,
            card: null,
            error: '',
            loading: false,
            complete: false,
        }
    },
    async mounted() {
        const vm = this
        await vm.init()
        if(vm.defaultName) {
            vm.holderName = vm.defaultName
        } else if (vm.account && vm.account.name) {
            vm.holderName = vm.account.name
        }
    },
    methods: {
        async init() {
            const vm = this
            const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)
            vm.stripe = stripe
            const elements = stripe.elements({
                locale: vm.$i18n.locale,
                fonts: [{cssSrc: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap'}]
            });
            const card = elements.create('card', {style: vm.style});
            card.mount('#card-element');
            card.on('change', vm.onCardChange);
            vm.card = card

        },
        onCardChange(event) {
            const vm = this
            vm.complete = event.complete

            if (event.error) {
                return vm.error = event.error.message;
            }

            vm.error = '';
        },
        async send() {
            const vm = this
            return await vm.withLoading(async () => {
                const result = await vm.stripe.createPaymentMethod({
                    type: 'card',
                    card: vm.card,
                    billing_details: {name: vm.holderName}
                })
                if (result.error) {
                    vm.error = result.error.message;
                    return false
                }
                const saved = await savePaymentMethod(result.paymentMethod.id)
                if (!saved.ok) {
                    vm.error = vm.$t(saved.messageKey)
                    return false
                }
                vm.stripeTokenHandler(result);
                return true
            })
        },
        stripeTokenHandler(token) {
            // console.log('card token created!', token)
            const vm = this
            vm.$emit('success', token)
        }
    },
    computed: {
        ...mapState(['account']),
        style(vm = this) {
            const dark = vm.$vuetify.theme.dark
            return {
                base: {
                    color: dark ? '#F2F5FA' : '#1A202C',
                    fontFamily: 'Outfit, "Roboto", sans-serif',
                    fontSmoothing: 'antialiased',
                    fontSize: '14px',
                    fontWeight: '400',
                    iconColor: '#66CCCC',
                    '::placeholder': {
                        color: dark ? '#556070' : '#A0AEC0'
                    }
                },
                invalid: {
                    color: '#E85C5C',
                    iconColor: '#E85C5C'
                }
            }
        },
        payload(vm = this) {
            if (vm.complete) {
                return {
                    type: 'card',
                    card: vm.card,
                    billing_details: {name: vm.holderName}
                }
            } else {
                return null
            }
        }
    },
    watch: {
        '$vuetify.theme.dark'() {
            if (this.card) this.card.update({style: this.style})
        },
        account(val) {
            if (val) {
                this.holderName = val.name
            }
        },
        error(val) {
            if (val) {
                this.$emit('error', val)
            }
        },
        holderName(val) {
            if (!val) {
                this.$emit('error', val)
            }
        },
        payload(val) {
            this.$emit('cardData', val)
        }
    }
}
</script>

<style scoped>

#card-element {
    box-sizing: border-box;
    padding: 14px 14px;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    background-color: var(--v-field-base);
    transition: border-color 150ms ease;
    height: 48px;
}

.StripeElement--focus {
    border-color: var(--v-accent-base);
    background-color: var(--v-surface-base);
}

.StripeElement--invalid {
    border-color: var(--v-error-base);
}

.card-creator {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.cr-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.2;
    color: var(--v-txtPrimary-base);
}

.cr-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.cr-field {
    display: flex;
    flex-direction: column;
}

.cr-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.cr-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 8px;
    background: rgba(232, 92, 92, 0.12);
    border: 1px solid var(--v-error-base);
    color: var(--v-error-base);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
}

.cr-save {
    background: var(--brand-gradient) !important;
}

.cr-save.v-btn--disabled {
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtMuted-base) !important;
}

</style>